import { Component, Injector, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthSessionService } from '@app/core/auth';
import { GlobalComponent } from '@app/core/models';
import { CommonService, LocalStorageService } from '@app/core/services';
import * as theme from 'src/assets/json/themes.json';
import { Store } from '@ngxs/store';
import { GetConnectLink, GetModuleRedirectionURL, ProductState, UpdateThemeColor } from '@app/core/store';
import { environment } from '@environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { RoutingPath } from '@app/core/enums';
import { take } from 'rxjs';
declare const tinycolor: any;
export interface Color {
  name: string;
  hex: string;
  darkContrast: boolean;
}

interface SpAuthContext {
  userId?: string;
  businessId?: number;
  cbCustomerId?: string;
  externalUserGuid?: string;
  themeColor?: string;
  companyType?: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
  environment = environment;
  helpDeskUrl = environment.helpDeskUrl;
  menuData = [
    {
      icon: 'space_dashboard',
      id: 1,
      name: 'Products',
      url: '/home/products',
    },
    {
      icon: 'shopping_basket',
      id: 2,
      name: 'Subscription',
      url: '/home/subscriptions',
    },
    {
      icon: 'receipt_long',
      id: 3,
      name: 'Billing History',
      url: '/home/billingistory',
    },
    {
      icon: 'payment',
      id: 4,
      name: 'Payment Methods',
      url: '/home/paymentmethods',
    },
    {
      icon: 'text_snippet',
      id: 5,
      name: 'Invoices',
      url: '/home/invoices',
    },
  ];

  // Multicolor theme
  colorSelected = '#6d41a1';
  singletheme: any;
  themes = JSON.parse(JSON.stringify(theme));
  colors: string[] = [
    '#6d41a1',
    '#6661b8',
    '#4461d7',
    '#a0144f',
    '#045b62',
    '#14539a',
    '#a76f05',
    '#000000',
  ];

  primaryColor: string;
  primaryColorPalette: Color[] = [];
  commonService: CommonService;
  // End Intalization of Muticolor theme
  subscriptionModuleList: any = [];
  myAdminUrl: string;
  profileUrl: any = null;
  ecoSpaceLogo: any = this.environment.imagePath + '/capium-logo.png';
  favIcon: any = this.environment.imagePath + '/company-logo.svg';
  userData: any;
  totalCartItem = 0;
  isSME: boolean;
  constructor(
    public authSession: AuthSessionService,
    private router: Router,
    public globalComponent: GlobalComponent,
    private store: Store,
    private injector: Injector,
    public storage: LocalStorageService,
    private cookieService: CookieService,
    private http: HttpClient
  ) {
    this.commonService = this.injector.get<CommonService>(CommonService);
    this.onSelectionChange(this.globalComponent.themecolor(), false);

    this.globalComponent.totalCartItem.subscribe((x) => {
      this.totalCartItem = x;
    });
  }

  ngOnInit(): void {
    this.authSession.userData$().pipe(take(1)).subscribe((userData) => {
      if (userData) {
        this.setUserData(userData);
      }
    });
  }

  private setUserData(data: any): void {
    this.userData = data;
    const companyType =
      this.userData.companyType || this.userData.company_type || '';
    // Only set isSME when we know company type. Empty → wait for /auth/context.
    // Missing type previously defaulted to SME and hid accountant SubscriptionMaster data.
    if (companyType) {
      this.isSME = !this.isAccountantCompanyType(companyType);
      this.storage.set('isSME', this.isSME);
    }
    // Never use OneLogin/Identity sub as Chargebee customer id.
    const cbFromClaim =
      this.userData.subcriptionClientId ||
      this.userData.subscriptionClientId ||
      this.userData.chargebecustomerId ||
      this.userData.customer_id;
    if (cbFromClaim) {
      this.cookieService.set('customerId', cbFromClaim, undefined, '/');
    }
    const externalGuid =
      this.userData.ExternalUserGUID || this.userData.external_user_guid;
    if (externalGuid) {
      this.cookieService.set('externalUserGUID', externalGuid, undefined, '/');
    }
    this.bootstrapSpAuthContext();
    if (!this.isSME) {
      this.getConnectLink();
    }
  }

  /** Resolve Chargebee / SP UniversalId from API (thin OneLogin JWT has no CB claim). */
  private bootstrapSpAuthContext(): void {
    const url = `${environment.apiVersion2Url}auth/context`;
    this.http.get<SpAuthContext>(url).pipe(take(1)).subscribe({
      next: (ctx) => {
        if (ctx?.userId) {
          this.cookieService.set('userId', String(ctx.userId), undefined, '/');
        }
        const cbCustomerId = ctx?.cbCustomerId || (ctx as any)?.CbCustomerId;
        if (cbCustomerId) {
          this.cookieService.set('customerId', cbCustomerId, undefined, '/');
        } else {
          // Clear bad fallback (OneLogin sub) if previously stored
          const existing = this.cookieService.get('customerId');
          if (existing && existing === (this.userData?.sub || '')) {
            this.cookieService.delete('customerId', '/');
          }
        }
        const externalUserGuid =
          ctx?.externalUserGuid || (ctx as any)?.ExternalUserGuid;
        if (externalUserGuid) {
          this.cookieService.set(
            'externalUserGUID',
            externalUserGuid,
            undefined,
            '/'
          );
        }
        sessionStorage.setItem('spAuthContextLoadedAt', String(Date.now()));
        if (ctx?.themeColor) {
          this.cookieService.set('themecolor', ctx.themeColor, undefined, '/');
        }
        if (ctx?.companyType) {
          const wasSme = this.isSME;
          this.isSME = !this.isAccountantCompanyType(ctx.companyType);
          this.storage.set('isSME', this.isSME);
          // Subscriptions/products may have already loaded on the wrong (SME) API path.
          if (wasSme === true && this.isSME === false) {
            const path = window.location.pathname || '';
            if (
              path.includes('/home/subscription') ||
              path.includes('/home/products')
            ) {
              window.location.reload();
            }
          }
        }
      },
      error: (err) => {
        console.error('[Auth] Failed to load SP auth/context', err);
      },
    });
  }

  /** OneLogin.Firms.CompanyType is "Accountants"; do not confuse with user role "Accountant". */
  private isAccountantCompanyType(companyType: string): boolean {
    const t = (companyType || '').trim().toLowerCase();
    return t === 'accountants' || t === 'accountant';
  }

  logout(): void {
    this.authSession.logout(() => {
      this.cookieService.deleteAll('/');
      localStorage.clear();
      sessionStorage.clear();
    });
  }

  onLetConnect(): void {
    this.router.navigate([RoutingPath.letsConnect]);
  }

  goToProfile(): void {
    window.location.href = this.authSession.profileUrl;
  }

  //Event for Multicolor theme
  onSelectionChange(event: any, isThemeColor: boolean) {
    this.colorSelected = event;
    this.primaryColor = event;

    this.computeColors(this.primaryColor);

    if (isThemeColor) {
      this.store.dispatch(new UpdateThemeColor(this.primaryColor)).subscribe();
    }
  }

  computeColors(hex: string): void {
    for (let index = 0; index < Object.keys(this.themes).length - 2; index++) {
      if (this.themes[index].id === hex) {
        const selectedThemeArray = this.themes[index];

        this.primaryColorPalette = [
          this.getColorObject(selectedThemeArray?.id, '500'),
          this.getColorObject(selectedThemeArray?.color50, '50'),
          this.getColorObject(selectedThemeArray?.color100, '100'),
          this.getColorObject(selectedThemeArray?.color200, '200'),
          this.getColorObject(selectedThemeArray?.color300, '300'),
        ];
      }
    }

    for (const color of this.primaryColorPalette) {
      const key = `--theme-primary-${color.name}`;
      const value = color.hex;
      document.documentElement.style.setProperty(key, value);
    }
  }

  getColorObject(value, name): Color {
    const c = tinycolor(value);
    return {
      name: name,
      hex: c.toHexString(),
      darkContrast: c.isLight(),
    };
  }

  //   retrieveSubscription(): void {
  //     this.store.dispatch(new RetrieveSubscription()).subscribe(
  //       (res) => {
  //         this.subscriptionModuleList =
  //           res.subscription.retrieveSubscriptionList.data.listOfAddons ?? [];
  //             this.profileUrl =
  //             res.subscription.retrieveSubscriptionList.data.picture;
  //         this.subscriptionModuleList.forEach((element) => {
  //           if (element.externalName === 'My Admin') {
  //             this.myAdminUrl = element.appUrl;
  //           }
  //         });

  //       }
  //     );
  //   }

  getConnectLink(): void {


    const userName = this.userData.email;

    this.store.dispatch(new GetConnectLink(userName)).subscribe((res) => {
      console.log(res);
      this.subscriptionModuleList = res.product.connectLinkList ?? [];
      this.profileUrl = res.product.profilePic ?? null;
      this.ecoSpaceLogo = res.product.ecoSpaceLogo
        ? res.product.ecoSpaceLogo
        : this.environment.imagePath + '/capium-logo.png';

      this.favIcon = res.product.favIcon
        ? res.product.favIcon
        : this.environment.imagePath + '/company-logo.svg';
      this.subscriptionModuleList.forEach((element) => {
        if (element.externalName === 'My Admin') {
          this.myAdminUrl = element.appUrl;
        }
      });
      this.subscriptionModuleList.sort((a, b) => a.orderNo - b.orderNo);
    });
    console.log(this.ecoSpaceLogo);
  }
  appUrl = '';
  redirectToModule(subscriptionModule, event: Event): void {
    if (subscriptionModule.isDisabled) {
      this.appUrl = '';
      event.preventDefault();
    } else {
      //this.appUrl = subscriptionModule.appUrl;
      const externalUserGUID = this.globalComponent.externalUserGUID();
      const email = this.userData.email;
      this.store
        .dispatch(
          new GetModuleRedirectionURL(
            subscriptionModule['addonId'],
            email,
            externalUserGUID
          )
        )
        .subscribe(() => {
          this.appUrl = this.store.selectSnapshot(
            ProductState.getModuleRedirectionURL
          );

          window.location.replace(this.appUrl);
        });
    }
  }

  redirectToHome(): void {
    window.location.replace(this.authSession.homeUrl);
  }

  myAdminClick(event: Event): void {
    const module = this.subscriptionModuleList.find(
      (element) => element.externalName === 'My Admin'
    );
    this.redirectToModule(module, event);
  }
  //End of multicolor theme code
  onHelpClick(): void {
    window.open(this.helpDeskUrl, '_blank');
  }
}
